<template>
  <v-row>
    <v-col>
      <v-row align="center" class="mb-4">
        <v-col>
          <v-alert
            v-if="saveError"
            type="error"
            dense
            class="mb-0"
          >
            Não foi possível salvar no momento. Tente novamente.
          </v-alert>

          <v-alert
            v-else-if="hasUnsavedChanges"
            type="warning"
            dense
            class="mb-0"
          >
            <strong>
              Alterações não salvas. Salve antes de mudar de aba.
            </strong>
          </v-alert>
        </v-col>

        <v-col cols="auto">
          <v-btn
            color="primary"
            :loading="saving"
            :disabled="
              disabled ||
              saving ||
              !hasUnsavedChanges
            "
            @click="saveSurgeryPostoperative"
          >
            <v-icon left>
              mdi-content-save
            </v-icon>

            Salvar
          </v-btn>
        </v-col>
      </v-row>

      <Allergy
        :attendance="attendance"
        :showForm="false"
        :allowDelete="false"
      />

      <SurgeryDetail
        :surgery="surgery"
      />

      <SurgeryVitalMeasurementList
        :surgery="surgery"
        :refreshSurgery="refreshSurgery"
        :surgeryMoment="surgeryMoment"
        :disabled="disabled"
      />

      <SurgeryTherapyList
        :surgery="surgery"
        :refreshSurgery="refreshSurgery"
        :surgeryMoment="surgeryMoment"
        :disabled="disabled"
      />

      <v-row>
        <v-col>
          <v-textarea
            v-model="observation"
            label="Notas"
            placeholder=" "
            hide-details
            outlined
            background-color="white"
            :disabled="disabled"
            @input="handleChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="text-h5 gray--text">
          Alta cirúrgica
        </v-col>
      </v-row>

      <v-row align="end">
        <v-col cols="2">
          <v-row dense>
            <v-col>
              Removido acesso venoso
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col>
              <v-radio-group
                v-model="removedVenousAccess"
                row
                mandatory
                dense
                hide-details
                class="mt-0"
                :disabled="disabled"
                @change="handleChange"
              >
                <v-radio
                  label="Sim"
                  :value="true"
                />

                <v-radio
                  label="Não"
                  :value="false"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="2">
          <v-row dense>
            <v-col>
              Removido eletrodos
            </v-col>
          </v-row>

          <v-row no-gutters>
            <v-col>
              <v-radio-group
                v-model="removedElectrodes"
                row
                mandatory
                dense
                hide-details
                class="mt-0"
                :disabled="disabled"
                @change="handleChange"
              >
                <v-radio
                  label="Sim"
                  :value="true"
                />

                <v-radio
                  label="Não"
                  :value="false"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="5">
          <v-checkbox
            v-model="patientReceivedPrescription"
            label="Entregue receituário de pós-operatório ao paciente"
            class="mt-0"
            hide-details
            dense
            :disabled="disabled"
            @change="handleChange"
          />
        </v-col>

        <v-col cols="3">
          <v-checkbox
            v-model="medicalRelease"
            label="Conceder alta para o paciente"
            class="mt-0"
            hide-details
            dense
            :disabled="disabled"
            @change="handleChange"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col class="text-h5 gray--text">
          Anexos
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <FileUpload
            ownerType="Surgery::Postoperative"
            :ownerId="surgery.postoperative.id"
            :disabled="disabled"
          />
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import Allergy from "@/components/medical_record/hpp/allergy.vue";
import SurgeryDetail from "@/components/surgery/detail.vue";
import SurgeryVitalMeasurementList from "@/components/surgery/vital_measurement/list/index.vue";
import SurgeryTherapyList from "@/components/surgery/therapy/list/index.vue";

export default {
  name: "SurgeryPostoperative",

  props: {
    surgery: Object,
    refreshSurgery: Function,
    setSurgeryMoment: Function,
    surgeryMoment: String,

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  components: {
    Allergy,
    SurgeryDetail,
    SurgeryVitalMeasurementList,
    SurgeryTherapyList,
  },

  data() {
    return {
      initializing: true,
      saving: false,
      saveError: null,
      initialSnapshot: null,

      medicalRelease: false,
      removedVenousAccess: false,
      removedElectrodes: false,
      patientReceivedPrescription: false,
      observation: "",
    };
  },

  computed: {
    attendance() {
      return this.surgery.attendance;
    },

    postoperative() {
      return this.surgery.postoperative || {};
    },

    /*
     * O estado dirty é calculado comparando o formulário
     * atual com o estado originalmente carregado.
     */
    hasUnsavedChanges() {
      if (
        this.initializing ||
        this.initialSnapshot === null
      ) {
        return false;
      }

      return (
        this.createSnapshot() !==
        this.initialSnapshot
      );
    },
  },

  async mounted() {
    this.initializing = true;

    this.medicalRelease =
      this.postoperative.medicalRelease ?? false;

    this.removedVenousAccess =
      this.postoperative.removedVenousAccess ?? false;

    this.removedElectrodes =
      this.postoperative.removedElectrodes ?? false;

    this.patientReceivedPrescription =
      this.postoperative
        .patientReceivedPrescription ?? false;

    this.observation =
      this.postoperative.observation || "";

    this.setSurgeryMoment("postoperative");

    await this.$nextTick();

    /*
     * O estado completamente carregado passa a ser
     * a referência para identificar alterações.
     */
    this.captureSnapshot();

    this.initializing = false;
  },

  methods: {
    normalizeText(value) {
      return String(value || "")
        .replace(/\r\n/g, "\n");
    },

    createSnapshot() {
      return JSON.stringify({
        medicalRelease:
          Boolean(this.medicalRelease),

        removedVenousAccess:
          Boolean(this.removedVenousAccess),

        removedElectrodes:
          Boolean(this.removedElectrodes),

        patientReceivedPrescription:
          Boolean(
            this.patientReceivedPrescription
          ),

        observation: this.normalizeText(
          this.observation
        ),
      });
    },

    captureSnapshot() {
      this.initialSnapshot =
        this.createSnapshot();
    },

    handleChange() {
      if (
        this.initializing ||
        this.disabled ||
        this.saving
      ) {
        return;
      }

      /*
       * hasUnsavedChanges é computed.
       * Aqui apenas limpamos um erro anterior quando
       * o operador inicia uma nova alteração.
       */
      this.saveError = null;
    },

    async saveSurgeryPostoperative() {
      if (
        this.disabled ||
        this.saving ||
        !this.hasUnsavedChanges
      ) {
        return;
      }

      this.saving = true;
      this.saveError = null;

      /*
       * Guarda exatamente o estado que será enviado.
       * Caso o operador altere algo durante a request,
       * o novo conteúdo continuará marcado como dirty.
       */
      const snapshotBeingSaved =
        this.createSnapshot();

      try {
        await this.apolloMutate({
          mutation: require(
            "@/graphql/surgery/save_surgery_postoperative.gql"
          ),

          variables: {
            surgeryId: this.surgery.id,

            medicalRelease:
              this.medicalRelease,

            removedVenousAccess:
              this.removedVenousAccess,

            removedElectrodes:
              this.removedElectrodes,

            patientReceivedPrescription:
              this.patientReceivedPrescription,

            observation:
              this.observation,
          },
        });

        /*
         * O estado enviado passa a ser o novo baseline.
         */
        this.initialSnapshot =
          snapshotBeingSaved;

        /*
         * Atualiza os dados gerais da cirurgia somente
         * depois que a mutation foi concluída.
         */
        await this.refreshSurgery();
      } catch (error) {
        this.saveError = error;

        console.error(
          "Erro ao salvar pós-operatório:",
          error
        );
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped></style>
